import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { UserEntity } from 'Models/Entities';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import LoaderInsideButton from 'Views/Components/Spinner/LoaderInsideButton';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { RegisterCustomerFields } from './RegisterCustomerFields';
import { useRegisterCustomerForm } from './useRegisterCustomerForm';

export interface RegisterCustomerStepProps {
	/** Id of the form so a modal footer button outside the form can submit it (buttonProps={{ form: formId }}). */
	formId: string;
	/** Called with the created customer (id populated) once registration succeeds. */
	onSuccess: (customer: UserEntity) => void;
	onLoadingChange?: (loading: boolean) => void;
}

/**
 * Inline "register a new customer" step for the purchase modals (parking, gift voucher,
 * anytime ticket). Works like the modals' login step: the step body renders a form with a known
 * id, and the modal footer submits it from outside via RegisterCustomerStepFooter.
 */
function RegisterCustomerStepContent({ formId, onSuccess, onLoadingChange }: RegisterCustomerStepProps) {
	const { userEntity, errors, loading, registerAsCompany, setRegisterAsCompany, revalidate, submit } =
		useRegisterCustomerForm();

	useEffect(() => {
		onLoadingChange?.(loading);
	}, [loading, onLoadingChange]);

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const customer = await submit();
		if (customer) {
			alertToast(`Customer created. An account setup email has been sent to ${customer.email}.`, 'success');
			onSuccess(customer);
		}
	};

	return (
		<form id={formId} onSubmit={onSubmit} className="register-customer-step">
			<h5>Register a new customer</h5>
			<RegisterCustomerFields
				userEntity={userEntity}
				errors={errors}
				onAfterChange={revalidate}
				registerAsCompany={registerAsCompany}
				onRegisterAsCompanyChange={setRegisterAsCompany}
			/>
		</form>
	);
}

export const RegisterCustomerStep = observer(RegisterCustomerStepContent);

export interface RegisterCustomerStepFooterProps {
	formId: string;
	onBack: () => void;
	loading?: boolean;
}

/** Modal footer for RegisterCustomerStep — Back plus a Register button that submits the step's form. */
export function RegisterCustomerStepFooter({ formId, onBack, loading = false }: RegisterCustomerStepFooterProps) {
	return (
		<NewModal.Footer>
			<Button display={Display.Outline} colors={Colors.Grey} newStyles onClick={onBack} disabled={loading}>
				Back
			</Button>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				newStyles
				type="submit"
				disabled={loading}
				buttonProps={{ form: formId, 'data-test-id': 'register-customer-submit' } as any}
			>
				Register
				{loading && <LoaderInsideButton />}
			</Button>
		</NewModal.Footer>
	);
}
